/**
 * Hook for fetching communities filtered by city
 * @module hooks/use-communities
 */

import { useState, useEffect, useCallback } from 'react';
import { getCommunities } from '@/lib/api/initiatives';
import type { CommunityLookup } from '@/types/initiative';

export interface UseCommunitiesReturn {
  communities: CommunityLookup[];
  loading: boolean;
  error: string | null;
}

export function useCommunities(cityId?: string): UseCommunitiesReturn {
  const [communities, setCommunities] = useState<CommunityLookup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCommunities = useCallback(async () => {
    if (!cityId) {
      setCommunities([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await getCommunities(cityId);
      if (response.success && response.data) {
        setCommunities(response.data);
      } else {
        setError(response.message || 'Failed to load communities');
        setCommunities([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setCommunities([]);
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    fetchCommunities();
  }, [fetchCommunities]);

  return { communities, loading, error };
}
